import React, { useState, useEffect, useCallback } from 'react';
import { fetchClientPOT, updateClientPOT, apiFetch } from '../../utils/api';

// Rule types definition for the dropdown
const RULE_TYPES = [
  { value: 'data_verdict_rule', label: 'Data Verdict (Este cerere de facturare?)' },
  { value: 'beneficiary_extraction', label: 'Extragerea beneficiarului (Pasul 1)' },
  { value: 'beneficiary_catalog_matching', label: 'Maparea beneficiarului in Catalog (Pasul 2A)' },
  { value: 'product_specification_check', label: 'Verificare specificare produse (Pasul 1A)' },
  { value: 'product_catalog_matching', label: 'Maparea Produsului in Catalog (Pasul 1B)' },
  { value: 'temporal_classification', label: 'Clasificare temporala (Produs nespecificat)' },
  { value: 'invoice_number_extraction', label: 'Extragerea numarului facturii' },
  { value: 'date_extraction', label: 'Extragerea datei' }
];

export default function POTEditor({ clientId, onUpdate }) {
  // Data state
  const [potData, setPotData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // UI state
  const [showForm, setShowForm] = useState(false);
  const [editingKey, setEditingKey] = useState(null); // Key being edited (null = new)
  const [formData, setFormData] = useState({
    key: '',
    instruction: '',
    include_catalog: true,
    quantity_type: ''
  });
  
  // Preview state
  const [previewKey, setPreviewKey] = useState(null);
  const [previewText, setPreviewText] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);

  const loadPOT = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await fetchClientPOT(clientId);
      setPotData(data || {});
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [clientId]);

  // Load initial data
  useEffect(() => {
    loadPOT();
  }, [loadPOT]);

  // --- Machine-drafted suggestions queue (learning loop) ---
  const [suggestions, setSuggestions] = useState([]);
  const [suggBusy, setSuggBusy] = useState(null);

  const loadSuggestions = useCallback(async () => {
    try {
      const res = await apiFetch(`/api/clients/${clientId}/pot-suggestions`);
      if (res.ok) {
        const d = await res.json();
        setSuggestions(d.suggestions || []);
      }
    } catch (_) { /* non-blocking */ }
  }, [clientId]);

  useEffect(() => { loadSuggestions(); }, [loadSuggestions]);

  const decideSuggestion = async (id, action) => {
    setSuggBusy(id);
    try {
      const res = await apiFetch(`/api/pot-suggestions/${id}/decide`, {
        method: 'POST',
        body: JSON.stringify({ action }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d?.msg || 'Eroare la decizie');
      }
      setSuccess(action === 'approve'
        ? 'Sugestie aprobată — regula a fost adăugată în POT.'
        : 'Sugestie respinsă — nu va mai fi propusă.');
      await Promise.all([loadPOT(), loadSuggestions()]);
    } catch (e) {
      setError(e.message);
    } finally {
      setSuggBusy(null);
    }
  };

  // --- Form Handlers ---

  const handleEdit = (key) => {
    const rule = potData[key];
    setEditingKey(key);
    setFormData({
      key: key,
      instruction: rule.instruction || '',
      include_catalog: rule.include_catalog ?? true,
      quantity_type: rule.quantity_type || ''
    });
    setShowForm(true);
    setPreviewKey(null); // Close preview if open
  };

  const handleAddNew = () => {
    setEditingKey(null);
    setFormData({
      key: RULE_TYPES[0].value, // Default to first type
      instruction: '',
      include_catalog: true,
      quantity_type: ''
    });
    setShowForm(true);
    setPreviewKey(null);
  };

  const handleDelete = async (key) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) return;
    
    const updatedData = { ...potData };
    delete updatedData[key];
    
    await saveToServer(updatedData);
  };

  const handleFormSave = async () => {
    // Validation
    if (!formData.key) return setError('Please select a rule type');
    if (!formData.instruction.trim()) return setError('Instruction cannot be empty');

    const updatedData = { ...potData };
    
    // If editing a key and the key changed (shouldn't happen with disabled input, but good practice)
    if (editingKey && editingKey !== formData.key) {
      delete updatedData[editingKey];
    }

    const rulePayload = {
      instruction: formData.instruction,
      include_catalog: formData.include_catalog
    };
    // Only save quantity_type for temporal_classification rules
    if (formData.key === 'temporal_classification' && formData.quantity_type) {
      rulePayload.quantity_type = formData.quantity_type;
    }
    updatedData[formData.key] = rulePayload;

    const success = await saveToServer(updatedData);
    if (success) {
      setShowForm(false);
    }
  };

  const saveToServer = async (newData) => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await updateClientPOT(clientId, newData);
      setPotData(newData);
      setSuccess('Rules updated successfully!');
      setTimeout(() => setSuccess(null), 3000);
      
      // Notify parent component to refresh client data (including POT status)
      if (onUpdate) {
        onUpdate();
      }
      
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setIsLoading(false);
    }
  };
  
  const handlePreview = async (key) => {
    if (previewKey === key) {
      setPreviewKey(null); // Toggle off
      return;
    }
    
    setPreviewKey(key);
    setLoadingPreview(true);
    setPreviewText('Loading context...');
    
    try {
      const res = await apiFetch(`/api/clients/${clientId}/pot/preview?key=${key}`);
      if (!res.ok) throw new Error('Failed to fetch preview');
      const data = await res.json();
      setPreviewText(data.preview);
    } catch (e) {
      setPreviewText(`Error loading preview: ${e.message}`);
    } finally {
      setLoadingPreview(false);
    }
  };

  // --- Render Helpers ---

  const getLabelForKey = (key) => {
    const rule = RULE_TYPES.find(r => r.value === key);
    return rule ? rule.label : key;
  };

  return (
    <div className="section-card">
      <div className="section-header">
        <h2 className="section-title">Lectii pentru AI: Packet of Truth (POT)</h2>
        <p className="section-subtitle">Contine informatii din care agentul AI invata lucruri specifice despre acest emitent.</p>
        <div>
          {!showForm && (
            <button 
              className="review-btn" 
              onClick={handleAddNew}
              style={{ backgroundColor: '#d3c084', color: '#26221a', fontWeight: 'bold' }}
            >
              + Add Rule
            </button>
          )}
        </div>
      </div>

      <div className="section-content">

        {/* Machine-drafted suggestions awaiting the accountant's decision */}
        {suggestions.length > 0 && (
          <div style={{
            marginBottom: '1.25rem', padding: '1rem',
            backgroundColor: 'rgba(211, 192, 132, 0.06)',
            border: '1px solid rgba(211, 192, 132, 0.3)', borderRadius: '12px'
          }}>
            <div style={{ fontWeight: 700, marginBottom: '0.6rem' }}>
              Sugestii de reguli (din corecțiile dvs.) — {suggestions.length} în așteptare
            </div>
            {suggestions.map((s) => (
              <div key={s.id} style={{ marginBottom: '0.8rem', paddingBottom: '0.8rem',
                                       borderBottom: '1px solid rgba(211,192,132,0.15)' }}>
                <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 1, opacity: 0.7 }}>
                  {s.rule_key}
                  {s.evidence?.support ? ` · dovadă: ${s.evidence.support} facturi` : ''}
                  {s.evidence?.pattern ? ` · ${s.evidence.pattern}` : ''}
                </div>
                <div style={{ margin: '0.35rem 0 0.6rem' }}>{s.suggested_instruction}</div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button className="review-btn" disabled={suggBusy === s.id}
                          style={{ backgroundColor: '#d3c084', color: '#26221a', fontWeight: 'bold' }}
                          onClick={() => decideSuggestion(s.id, 'approve')}>
                    Aprobă
                  </button>
                  <button className="review-btn" disabled={suggBusy === s.id}
                          onClick={() => decideSuggestion(s.id, 'reject')}>
                    Respinge
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Error/Success Messages */}
        {error && (
          <div style={{ 
            marginBottom: '1rem', padding: '1rem', 
            backgroundColor: 'rgba(255, 100, 100, 0.1)', 
            border: '1px solid rgba(255, 100, 100, 0.3)', borderRadius: '8px', color: '#ff6b6b' 
          }}>
            <strong>Error:</strong> {error}
          </div>
        )}
        {success && (
          <div style={{ 
            marginBottom: '1rem', padding: '1rem', 
            backgroundColor: 'rgba(211, 192, 132, 0.1)', 
            border: '1px solid var(--primary-blue)', borderRadius: '8px', color: 'var(--primary-blue)' 
          }}>
            {success}
          </div>
        )}

        {/* --- LIST VIEW --- */}
        {!showForm && (
          <div style={{ display: 'grid', gap: '1rem' }}>
            {Object.keys(potData).length === 0 ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)', border: '1px dashed var(--border-color)', borderRadius: '8px' }}>
                No custom rules defined yet.
              </div>
            ) : (
              Object.entries(potData).map(([key, rule]) => (
                <div key={key} style={{ 
                  backgroundColor: 'rgba(211, 192, 132, 0.05)', 
                  border: '1px solid var(--border-color)', 
                  borderRadius: '12px', 
                  padding: '1.5rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#d3c084' }}>{getLabelForKey(key)}</h3>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button className="review-btn" onClick={() => handlePreview(key)} style={{ borderColor: 'var(--primary-blue)', color: 'var(--primary-blue)' }}>
                        {previewKey === key ? 'Hide Context' : 'Preview Context'}
                      </button>
                      <button className="review-btn" onClick={() => handleEdit(key)}>Edit</button>
                      <button className="review-btn" onClick={() => handleDelete(key)} style={{ color: '#ff6b6b', borderColor: 'rgba(255,100,100,0.3)' }}>Delete</button>
                    </div>
                  </div>
                  
                  <div style={{ 
                    backgroundColor: 'rgba(0,0,0,0.3)', 
                    padding: '1rem', 
                    borderRadius: '8px',
                    fontFamily: 'monospace',
                    fontSize: '0.9rem',
                    whiteSpace: 'pre-wrap',
                    color: '#f5f1e6'
                  }}>
                    {rule.instruction}
                  </div>
                  
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '0.2rem' }}>
                    Catalog Injection: <strong>{rule.include_catalog ? 'ON' : 'OFF'}</strong>
                    {key === 'temporal_classification' && rule.quantity_type && (
                      <span style={{ marginLeft: '1rem' }}>
                        Cantitate: <strong>{rule.quantity_type === 'working_days' ? 'Zile lucratoare' : rule.quantity_type}</strong>
                      </span>
                    )}
                  </div>
                  
                  {/* PREVIEW SECTION */}
                  {previewKey === key && (
                    <div style={{ 
                      marginTop: '1rem', 
                      backgroundColor: '#000', 
                      border: '1px dashed var(--border-color)', 
                      borderRadius: '8px',
                      padding: '1rem'
                    }}>
                      <div style={{ fontSize: '0.8rem', color: '#d3c084', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '1px' }}>
                        Previzualizare cunostinte folosite de agent
                      </div>
                      <div style={{ 
                        fontFamily: 'monospace', 
                        fontSize: '0.85rem', 
                        color: '#ccc', 
                        whiteSpace: 'pre-wrap', 
                        maxHeight: '300px', 
                        overflowY: 'auto' 
                      }}>
                        {previewText}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* --- FORM VIEW --- */}
        {showForm && (
          <div style={{ 
            backgroundColor: 'rgba(211, 192, 132, 0.05)', 
            border: '1px solid var(--primary-blue)', 
            borderRadius: '12px', 
            padding: '2rem'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '1.5rem', color: 'var(--text-primary)' }}>
              {editingKey ? 'Editeaza Regula' : 'Regula noua'}
            </h3>

            <div className="form-grid form-grid-1">
              {/* Rule Type Selector */}
              <div className="form-field">
                <label className="form-field-label">Tip Regula (Stadiul procesarii)</label>
                <select 
                  className="clients-input"
                  value={formData.key}
                  onChange={(e) => setFormData({...formData, key: e.target.value})}
                  disabled={!!editingKey} // Cannot change type when editing existing rule
                  style={{ opacity: editingKey ? 0.7 : 1 }}
                >
                  {RULE_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              {/* Instruction Text */}
              <div className="form-field">
                <label className="form-field-label">Instructiuni (Adevarul)</label>
                <textarea 
                  className="clients-input"
                  value={formData.instruction}
                  onChange={(e) => setFormData({...formData, instruction: e.target.value})}
                  placeholder="ex., Daca factura mentioneaza 'Consultanta', intodeauna este vorba despre Produsul cu ID 45."
                  style={{ minHeight: '150px', resize: 'vertical', lineHeight: '1.5' }}
                />
                <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                  Scrie text obisnuit. Nu este nevoie de vreo formatare speciala.
                </p>
              </div>

              {/* Include Catalog Toggle */}
              <div className="form-field" style={{ flexDirection: 'row', alignItems: 'center', gap: '12px' }}>
                <input
                  type="checkbox"
                  id="include_catalog"
                  checked={formData.include_catalog}
                  onChange={(e) => setFormData({...formData, include_catalog: e.target.checked})}
                  style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                />
                <label htmlFor="include_catalog" className="form-field-label" style={{ marginBottom: 0, cursor: 'pointer' }}>
                  Include Catalogul din Baza de date (Produse/Beneficiari) in Prompt
                </label>
              </div>

              {/* Quantity Type — only for temporal_classification */}
              {formData.key === 'temporal_classification' && (
                <div className="form-field">
                  <label className="form-field-label">Tip cantitate (optional)</label>
                  <select
                    className="clients-input"
                    value={formData.quantity_type}
                    onChange={(e) => setFormData({...formData, quantity_type: e.target.value})}
                  >
                    <option value="">Implicit (cantitate din document)</option>
                    <option value="working_days">Zile lucratoare (calcul automat calendar RO)</option>
                  </select>
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                    Daca produsul este facturat pe zile lucratoare, selecteaza "Zile lucratoare" pentru calcul automat.
                  </p>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '2rem' }}>
              <button className="review-btn" onClick={() => setShowForm(false)} disabled={isLoading}>
                Renunta
              </button>
              <button 
                className="review-btn" 
                onClick={handleFormSave} 
                disabled={isLoading}
                style={{ backgroundColor: '#d3c084', color: '#26221a', fontWeight: 'bold' }}
              >
                {isLoading ? 'Salveaza...' : 'Salveaza Regula'}
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
